export type ThemeMode = "light" | "dark" | "system";

const STORAGE_KEY = "theme";
const listeners = new Set<() => void>();
let mode: ThemeMode = readStored();

function readStored(): ThemeMode {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw === "light" || raw === "dark" || raw === "system" ? raw : "system";
}

function systemPrefersLight(): boolean {
  return window.matchMedia("(prefers-color-scheme: light)").matches;
}

/** `.light` on <html> is what index.css's `@theme inline` block switches on — dark is the
 *  bare `:root`, so "dark" and an unresolved system-prefers-dark both mean "no class". */
function applyClass(resolved: ThemeMode): void {
  document.documentElement.classList.toggle("light", resolved === "light");
}

function resolve(m: ThemeMode): ThemeMode {
  if (m !== "system") return m;
  return systemPrefersLight() ? "light" : "dark";
}

/**
 * Applies whatever is already in localStorage before React mounts, so the very first paint
 * uses the right palette — called once from main.tsx, synchronously, ahead of createRoot.
 */
export function initTheme(): void {
  applyClass(resolve(mode));
  window.matchMedia("(prefers-color-scheme: light)").addEventListener("change", () => {
    if (mode === "system") {
      applyClass(resolve(mode));
      emit();
    }
  });
}

export function getTheme(): ThemeMode {
  return mode;
}

export function setTheme(next: ThemeMode): void {
  mode = next;
  localStorage.setItem(STORAGE_KEY, next);
  applyClass(resolve(next));
  emit();
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit(): void {
  for (const listener of listeners) listener();
}
